import type {
  ArithOp,
  ScalarType,
  CompareOp,
  Expr,
  LogicOp,
  PrintEnd,
  Program,
  ReadKind,
  Stmt,
  ValueType,
} from '../ir.js';
import { arrayOf, elemOf, walkExpr, walkStmts, stmtExprs } from '../ir.js';
import { inferTypes } from '../types.js';
import type { LineRange } from '../writer.js';
import { lex, LexError, type Token } from './lex.js';
import type { Span, TextError, TextProgram } from './source.js';

const DECL_TYPE: Record<string, ValueType> = {
  int: 'int',
  long: 'int',
  double: 'double',
  float: 'double',
  string: 'string',
  char: 'char',
  bool: 'bool',
};

const READ_OF: Record<ScalarType, ReadKind> = {
  int: 'int',
  double: 'double',
  string: 'text',
  bool: 'int',
};

/** `cin >> a[i]` reads one place, so the kind comes from what the list holds. */
const readKindOf = (t: ValueType | undefined): ReadKind => READ_OF[elemOf(t ?? 'int')];

const COMPARE: Record<string, CompareOp> = {
  '==': '==',
  '!=': '!=',
  '<': '<',
  '<=': '<=',
  '>': '>',
  '>=': '>=',
};

/** C++ we deliberately do not support yet; naming them makes the error useful. */
const UNSUPPORTED_WORDS = new Set([
  'do',
  'switch',
  'case',
  'continue',
  'goto',
  'struct',
  'class',
  'void',
  'new',
  'delete',
  'printf',
  'scanf',
]);

class Fail extends Error {
  constructor(readonly info: TextError) {
    super(info.code);
  }
}

/**
 * The C++ a PebbleCode student writes: everything the C++ view generates, plus the ordinary ways
 * the same program is written by hand. Anything else is reported with a line and a reason rather
 * than guessed at.
 */
class CppParser {
  private i = 0;
  private readonly lineMap: Record<string, LineRange> = {};
  private readonly headMap: Record<string, LineRange> = {};
  private readonly spans: Record<string, Span> = {};
  private readonly declared = new Map<string, ValueType>();
  /** Ids of divisions written `(double) a / b`: true division even between whole numbers. */
  private readonly trueDiv = new Set<string>();
  /** Ids of operands a `(double)` cast was written in front of. */
  private readonly doubleCast = new Set<string>();
  private exprCount = 0;

  constructor(private readonly toks: Token[]) {}

  // ---------- token helpers ----------

  private get tok(): Token {
    return this.toks[this.i] ?? this.toks[this.toks.length - 1]!;
  }

  /** A method, not a comparison on `tok`, so narrowing never leaks between loops. */
  private atEnd(): boolean {
    return this.toks[this.i]?.kind === 'eof';
  }

  private at(text: string): boolean {
    return this.tok.text === text && this.tok.kind !== 'string' && this.tok.kind !== 'char';
  }

  private atAny(...texts: string[]): boolean {
    return texts.some((t) => this.at(t));
  }

  private next(): Token {
    const t = this.tok;
    if (this.i < this.toks.length - 1) this.i++;
    return t;
  }

  private eat(text: string): boolean {
    if (!this.at(text)) return false;
    this.i++;
    return true;
  }

  private expect(text: string): Token {
    if (!this.at(text)) this.fail('expected', { expected: text, found: this.tok.text || 'end' });
    return this.next();
  }

  private fail(code: TextError['code'], params?: Record<string, string>): never {
    const t = this.tok;
    throw new Fail({
      code,
      line: t.line,
      column: t.column,
      span: { from: t.from, to: Math.max(t.to, t.from + 1) },
      ...(params ? { params } : {}),
    });
  }

  // ---------- ids and maps ----------

  private stmtId(path: string): string {
    return `t:${path}`;
  }

  private exprId(stmtId: string): string {
    return `${stmtId}/e${++this.exprCount}`;
  }

  private mark(id: string, start: Token, endTok: Token, headEnd = endTok): void {
    this.lineMap[id] = [start.line, endTok.line];
    this.headMap[id] = [start.line, headEnd.line];
    this.spans[id] = { from: start.from, to: endTok.to };
  }

  private markExpr(e: Expr, start: Token, end: Token): void {
    this.lineMap[e.id] ??= [start.line, end.line];
    this.spans[e.id] ??= { from: start.from, to: end.to };
  }

  // ---------- frame ----------

  /** Skips includes, `using namespace std;` and finds the body of `int main()`. */
  parse(): Stmt[] {
    while (!this.atEnd()) {
      if (this.at('using')) {
        while (!this.at(';') && !this.atEnd()) this.next();
        this.eat(';');
        continue;
      }
      if (this.at('int') && this.toks[this.i + 1]?.text === 'main') {
        this.next();
        this.next();
        this.expect('(');
        while (!this.at(')') && !this.atEnd()) this.next();
        this.expect(')');
        this.expect('{');
        const body = this.statements('', '}');
        this.expect('}');
        return body;
      }
      this.next();
    }
    this.i = this.toks.length - 1;
    this.fail('no-main');
  }

  // ---------- statements ----------

  private statements(path: string, end: string): Stmt[] {
    const out: Stmt[] = [];
    while (!this.at(end) && !this.atEnd()) {
      this.statement(path, out);
    }
    return out;
  }

  /** One written statement; it may produce several IR statements (`cin >> a >> b;`). */
  private statement(path: string, out: Stmt[]): void {
    const start = this.tok;
    const id = this.stmtId(path === '' ? String(out.length) : `${path}.${out.length}`);

    if (this.tok.kind === 'ident' && UNSUPPORTED_WORDS.has(this.tok.text)) {
      this.fail('unsupported', { what: this.tok.text });
    }
    if (this.eat(';')) return;
    if (this.at('{')) {
      // A bare block: its statements simply join the surrounding list.
      this.next();
      while (!this.at('}') && !this.atEnd()) this.statement(path, out);
      this.expect('}');
      return;
    }
    if (this.at('return')) {
      this.next();
      while (!this.at(';') && !this.atEnd()) this.next();
      this.expect(';');
      return;
    }
    if (this.at('break')) {
      this.next();
      const semi = this.expect(';');
      const stmt: Stmt = { k: 'break', id };
      this.mark(id, start, semi);
      out.push(stmt);
      return;
    }
    if (this.at('if')) return this.ifStatement(id, path, out, start);
    if (this.at('while')) return this.whileStatement(id, path, out, start);
    if (this.at('for')) return this.forStatement(id, path, out, start);
    if (this.at('cin')) return this.cinStatement(path, out, start);
    if (this.at('cout')) return this.coutStatement(id, out, start);
    if (this.at('getline')) return this.getlineStatement(id, out, start);
    if (this.at('vector')) return this.vectorDeclaration(path, out, start);
    if (this.tok.kind === 'ident' && DECL_TYPE[this.tok.text] !== undefined) {
      return this.declaration(path, out, start);
    }
    return this.assignment(id, out, start);
  }

  /** `vector<int> a(n);` and `vector<vector<int>> a(n, vector<int>(m));` — lists and tables. */
  private vectorDeclaration(path: string, out: Stmt[], start: Token): void {
    this.next();
    this.expect('<');
    let rank: 1 | 2 = 1;
    if (this.at('vector')) {
      rank = 2;
      this.next();
      this.expect('<');
    }
    if (this.tok.kind !== 'ident' || DECL_TYPE[this.tok.text] === undefined) {
      this.fail('expected', { expected: 'a type', found: this.tok.text });
    }
    if (DECL_TYPE[this.tok.text] === 'char') this.fail('unsupported', { what: 'vector<char>' });
    const elem = elemOf(DECL_TYPE[this.next().text]!);
    // `>>` closes two levels at once, exactly as C++ reads it.
    if (rank !== 2) {
      this.expect('>');
    } else if (this.at('>>')) {
      this.next();
    } else {
      this.expect('>');
      this.expect('>');
    }
    if (this.tok.kind !== 'ident')
      this.fail('expected', { expected: 'a name', found: this.tok.text });
    const nameTok = this.next();
    this.declared.set(nameTok.text, arrayOf(elem, rank));

    const id = this.stmtId(path === '' ? String(out.length) : `${path}.${out.length}`);
    let dims: [Expr] | [Expr, Expr] | null = null;
    if (this.eat('(')) {
      const first = this.expression(id);
      if (rank === 2 && this.eat(',')) {
        // The inner `vector<int>(m)` only says how wide each row is.
        this.expect('vector');
        this.expect('<');
        this.next();
        this.eat('>');
        this.expect('(');
        const cols = this.expression(id);
        this.expect(')');
        dims = [first, cols];
      } else {
        dims = rank === 2 ? [first, this.num(id, 0)] : [first];
      }
      this.expect(')');
    }
    const semi = this.expect(';');
    if (dims) {
      out.push({ k: 'newArray', id, name: nameTok.text, elem, dims, base: 0 });
      this.mark(id, start, semi);
    }
  }

  private declaration(path: string, out: Stmt[], start: Token): void {
    let type = DECL_TYPE[this.next().text]!;
    // `long long` and `unsigned int` are still whole numbers.
    while (this.tok.kind === 'ident' && DECL_TYPE[this.tok.text] !== undefined) {
      type = DECL_TYPE[this.next().text]!;
    }
    do {
      if (this.tok.kind !== 'ident')
        this.fail('expected', { expected: 'a name', found: this.tok.text });
      const nameTok = this.next();
      if (this.at('[')) {
        // A `char s[100]` text is C's way; PebbleCode's texts are `string`.
        if (type === 'char') this.fail('unsupported', { what: 'char[]' });
        // `int a[100];` — the size is fixed in the text, so the list is made right here.
        const id = this.stmtId(path === '' ? String(out.length) : `${path}.${out.length}`);
        const dims: Expr[] = [];
        while (this.eat('[')) {
          dims.push(this.expression(id));
          this.expect(']');
        }
        const rank = dims.length === 2 ? 2 : 1;
        this.declared.set(nameTok.text, arrayOf(elemOf(type), rank));
        out.push({
          k: 'newArray',
          id,
          name: nameTok.text,
          elem: elemOf(type),
          dims: rank === 2 ? [dims[0]!, dims[1]!] : [dims[0]!],
          base: 0,
        });
        this.mark(id, start, this.tok);
        continue;
      }
      this.declared.set(nameTok.text, type);
      if (this.eat('=')) {
        const id = this.stmtId(path === '' ? String(out.length) : `${path}.${out.length}`);
        const value = this.expression(id);
        const stmt: Stmt = { k: 'set', id, name: nameTok.text, value };
        this.mark(id, start, this.tok);
        out.push(stmt);
      }
    } while (this.eat(','));
    this.expect(';');
  }

  private assignment(id: string, out: Stmt[], start: Token): void {
    if (this.atAny('++', '--')) {
      const op = this.next().text;
      const nameTok = this.next();
      const semi = this.expect(';');
      out.push(this.changeBy(id, nameTok.text, op === '++' ? 1 : -1));
      this.mark(id, start, semi);
      return;
    }
    if (this.tok.kind !== 'ident')
      this.fail('expected', { expected: 'a name', found: this.tok.text || 'end' });
    const nameTok = this.next();
    const name = nameTok.text;

    if (this.at('.') && this.toks[this.i + 1]?.text === 'assign') {
      this.next();
      this.next();
      this.expect('(');
      const rows = this.expression(id);
      this.expect(',');
      let cols: Expr | null = null;
      let elem = elemOf(this.declared.get(name) ?? 'int');
      if (this.at('vector')) {
        // A table: the second argument builds one row, and says how wide it is.
        this.next();
        this.expect('<');
        if (this.tok.kind === 'ident' && DECL_TYPE[this.tok.text] !== undefined) {
          elem = elemOf(DECL_TYPE[this.next().text]!);
        }
        this.expect('>');
        this.expect('(');
        cols = this.expression(id);
        if (this.eat(',')) this.expression(id);
        this.expect(')');
      } else {
        this.expression(id);
      }
      this.expect(')');
      const semi = this.expect(';');
      out.push({
        k: 'newArray',
        id,
        name,
        elem,
        dims: cols ? [rows, cols] : [rows],
        base: 0,
      });
      this.mark(id, start, semi);
      return;
    }

    if (this.at('[')) {
      const [index, index2] = this.indexes(id);
      if (this.atAny('++', '--')) {
        // `a[i]++` is the same as adding one to that place.
        const op = this.next().text;
        const semi = this.expect(';');
        out.push({
          k: 'setAt',
          id,
          name,
          index,
          index2,
          value: {
            k: 'arith',
            id: this.exprId(id),
            op: op === '++' ? '+' : '-',
            left: { k: 'at', id: this.exprId(id), name, index, index2 },
            right: this.num(id, 1),
          },
        });
        this.mark(id, start, semi);
        return;
      }
      const compound = this.atAny('+=', '-=', '*=', '/=', '%=') ? this.next().text : null;
      if (!compound) this.expect('=');
      const written = this.expression(id);
      const semi = this.expect(';');
      const value: Expr = compound
        ? {
            k: 'arith',
            id: this.exprId(id),
            op:
              compound === '+='
                ? '+'
                : compound === '-='
                  ? '-'
                  : compound === '*='
                    ? '*'
                    : compound === '/='
                      ? '/'
                      : 'mod',
            left: { k: 'at', id: this.exprId(id), name, index, index2 },
            right: written,
          }
        : written;
      out.push({ k: 'setAt', id, name, index, index2, value });
      this.mark(id, start, semi);
      return;
    }

    if (this.atAny('++', '--')) {
      const op = this.next().text;
      const semi = this.expect(';');
      out.push(this.changeBy(id, name, op === '++' ? 1 : -1));
      this.mark(id, start, semi);
      return;
    }
    if (this.atAny('+=', '-=', '*=', '/=', '%=')) {
      const op = this.next().text;
      const value = this.expression(id);
      const semi = this.expect(';');
      if (op === '+=' && this.declared.get(name) === 'string') {
        // `t += c` on a text adds to its end.
        out.push({ k: 'set', id, name, value: this.join(id, this.var(id, name), value) });
      } else if (op === '+=' || op === '-=') {
        const delta =
          op === '+='
            ? value
            : {
                k: 'arith' as const,
                id: this.exprId(id),
                op: '-' as ArithOp,
                left: this.num(id, 0),
                right: value,
              };
        out.push({ k: 'change', id, name, delta });
      } else {
        const arithOp: ArithOp = op === '*=' ? '*' : op === '/=' ? '/' : 'mod';
        out.push({
          k: 'set',
          id,
          name,
          value: {
            k: 'arith',
            id: this.exprId(id),
            op: arithOp,
            left: { k: 'var', id: this.exprId(id), name },
            right: value,
          },
        });
      }
      this.mark(id, start, semi);
      return;
    }
    this.expect('=');
    const value = this.expression(id);
    const semi = this.expect(';');
    out.push({ k: 'set', id, name, value });
    this.mark(id, start, semi);
  }

  private changeBy(id: string, name: string, by: number): Stmt {
    return { k: 'change', id, name, delta: this.num(id, by) };
  }

  private cinStatement(path: string, out: Stmt[], start: Token): void {
    this.next();
    do {
      this.expect('>>');
      if (this.tok.kind !== 'ident')
        this.fail('expected', { expected: 'a name', found: this.tok.text });
      const nameTok = this.next();
      const id = this.stmtId(path === '' ? String(out.length) : `${path}.${out.length}`);
      // `cin >> c` into a char takes one character, not a line; read a string and use s[0].
      if (this.declared.get(nameTok.text) === 'char') {
        this.fail('unsupported', { what: 'cin >> char' });
      }
      const as = readKindOf(this.declared.get(nameTok.text));
      if (this.at('[')) {
        // `cin >> a[i];` reads straight into one place of the list.
        const [index, index2] = this.indexes(id);
        out.push({
          k: 'setAt',
          id,
          name: nameTok.text,
          index,
          index2,
          value: { k: 'read', id: this.exprId(id), as },
        });
      } else {
        out.push({
          k: 'set',
          id,
          name: nameTok.text,
          value: { k: 'read', id: this.exprId(id), as },
        });
      }
      this.mark(id, start, this.toks[this.i - 1] ?? nameTok);
    } while (this.at('>>'));
    this.expect(';');
  }

  private getlineStatement(id: string, out: Stmt[], start: Token): void {
    this.next();
    this.expect('(');
    if (!this.eat('cin')) this.fail('expected', { expected: 'cin', found: this.tok.text });
    if (this.eat('>>')) this.expect('ws');
    this.expect(',');
    if (this.tok.kind !== 'ident')
      this.fail('expected', { expected: 'a name', found: this.tok.text });
    const nameTok = this.next();
    this.expect(')');
    const semi = this.expect(';');
    out.push({
      k: 'set',
      id,
      name: nameTok.text,
      value: { k: 'read', id: this.exprId(id), as: 'text' },
    });
    this.mark(id, start, semi);
  }

  private coutStatement(id: string, out: Stmt[], start: Token): void {
    this.next();
    const parts: Expr[] = [];
    let end: PrintEnd = 'none';
    while (this.eat('<<')) {
      if (this.at('endl') || (this.tok.kind === 'ident' && this.tok.text === 'endl')) {
        this.next();
        end = 'newline';
        continue;
      }
      const from = this.tok;
      const e = this.expression(id);
      this.markExpr(e, from, this.toks[this.i - 1] ?? from);
      parts.push(e);
    }
    const semi = this.expect(';');
    // A trailing " " or "\n" written as text is how the inline print block ends its line.
    const last = parts[parts.length - 1];
    if (end === 'none' && last?.k === 'text' && (last.value === ' ' || last.value === '\n')) {
      end = last.value === ' ' ? 'space' : 'newline';
      parts.pop();
    }
    const value =
      parts.length === 0
        ? this.text(id, '')
        : parts.reduce((a, b) => ({ k: 'join', id: this.exprId(id), left: a, right: b }));
    out.push({ k: 'print', id, value, end });
    this.mark(id, start, semi);
  }

  private ifStatement(id: string, path: string, out: Stmt[], start: Token): void {
    this.next();
    this.expect('(');
    const cond = this.expression(id);
    const close = this.expect(')');
    const branches: { cond: Expr; body: Stmt[] }[] = [];
    const childPath = (tag: string) =>
      path === '' ? `${out.length}${tag}` : `${path}.${out.length}${tag}`;
    const bodyEnd = this.block(childPath('b0'));
    branches.push({ cond, body: bodyEnd.body });
    let orElse: Stmt[] | null = null;
    let lastTok = bodyEnd.end;
    while (this.at('else')) {
      this.next();
      if (this.at('if')) {
        this.next();
        this.expect('(');
        const c = this.expression(id);
        this.expect(')');
        const b = this.block(childPath(`b${branches.length}`));
        branches.push({ cond: c, body: b.body });
        lastTok = b.end;
      } else {
        const b = this.block(childPath('e'));
        orElse = b.body;
        lastTok = b.end;
        break;
      }
    }
    out.push({ k: 'if', id, branches, orElse });
    this.mark(id, start, lastTok, close);
  }

  private whileStatement(id: string, path: string, out: Stmt[], start: Token): void {
    this.next();
    this.expect('(');
    const cond = this.expression(id);
    const close = this.expect(')');
    const childPath = path === '' ? String(out.length) : `${path}.${out.length}`;
    const body = this.block(childPath);
    out.push({ k: 'while', id, cond, body: body.body });
    this.mark(id, start, body.end, close);
  }

  private forStatement(id: string, path: string, out: Stmt[], start: Token): void {
    this.next();
    this.expect('(');
    const declaredHere = this.tok.kind === 'ident' && DECL_TYPE[this.tok.text] !== undefined;
    if (declaredHere) {
      const type = DECL_TYPE[this.next().text]!;
      if (this.tok.kind === 'ident') this.declared.set(this.tok.text, type);
    }
    if (this.tok.kind !== 'ident')
      this.fail('expected', { expected: 'a name', found: this.tok.text });
    const name = this.next().text;
    this.expect('=');
    const from = this.expression(id);
    this.expect(';');
    // The condition tells us the direction; only `name <= to` / `name >= to` are supported.
    if (this.tok.kind !== 'ident' || this.tok.text !== name) {
      this.fail('unsupported', { what: 'for' });
    }
    this.next();
    const cmp = this.next().text;
    if (cmp !== '<=' && cmp !== '>=' && cmp !== '<' && cmp !== '>')
      this.fail('unsupported', { what: 'for' });
    let to = this.expression(id);
    if (cmp === '<')
      to = { k: 'arith', id: this.exprId(id), op: '-', left: to, right: this.num(id, 1) };
    if (cmp === '>')
      to = { k: 'arith', id: this.exprId(id), op: '+', left: to, right: this.num(id, 1) };
    this.expect(';');
    const down = cmp === '>=' || cmp === '>';
    // The step: name++ / ++name / name-- / --name / name += 1 / name -= 1.
    this.forStep(name, down);
    const close = this.expect(')');
    const childPath = path === '' ? String(out.length) : `${path}.${out.length}`;
    const body = this.block(childPath);

    // `for (int k = 1; k <= n; k++)` whose counter is never used is the "repeat n times" block.
    const usesName = body.body.some((s) =>
      [...walkStmts([s])].some((x) =>
        stmtExprs(x).some((e) => [...walkExpr(e)].some((n) => n.k === 'var' && n.name === name)),
      ),
    );
    if (declaredHere && !usesName && !down && from.k === 'num' && from.value === 1) {
      out.push({ k: 'repeat', id, times: to, body: body.body });
    } else {
      out.push({ k: 'for', id, name, from, to, down, body: body.body });
    }
    this.mark(id, start, body.end, close);
  }

  private forStep(name: string, down: boolean): void {
    const want = down ? '--' : '++';
    if (this.atAny('++', '--')) {
      if (this.next().text !== want) this.fail('unsupported', { what: 'for' });
      if (this.tok.kind === 'ident' && this.tok.text === name) this.next();
      return;
    }
    if (this.tok.kind === 'ident' && this.tok.text === name) {
      this.next();
      if (this.atAny('++', '--')) {
        if (this.next().text !== want) this.fail('unsupported', { what: 'for' });
        return;
      }
      if (this.atAny('+=', '-=')) {
        const op = this.next().text;
        const step = this.tok;
        if (step.kind !== 'number' || step.text !== '1' || (op === '+=') === down) {
          this.fail('unsupported', { what: 'for' });
        }
        this.next();
        return;
      }
    }
    this.fail('unsupported', { what: 'for' });
  }

  /** A `{ … }` block or a single statement after if/while/for. */
  private block(path: string): { body: Stmt[]; end: Token } {
    if (this.eat('{')) {
      const body = this.statements(path, '}');
      const end = this.expect('}');
      return { body, end };
    }
    const body: Stmt[] = [];
    this.statement(path, body);
    return { body, end: this.toks[this.i - 1] ?? this.tok };
  }

  // ---------- expressions ----------

  private num(id: string, value: number): Expr {
    return { k: 'num', id: this.exprId(id), value };
  }

  private text(id: string, value: string): Expr {
    return { k: 'text', id: this.exprId(id), value };
  }

  private var(id: string, name: string): Expr {
    return { k: 'var', id: this.exprId(id), name };
  }

  private join(id: string, left: Expr, right: Expr): Expr {
    return { k: 'join', id: this.exprId(id), left, right };
  }

  private expression(id: string): Expr {
    return this.orExpr(id);
  }

  private binary(
    id: string,
    next: (id: string) => Expr,
    ops: string[],
    make: (op: string, left: Expr, right: Expr) => Expr,
  ): Expr {
    let left = next(id);
    while (
      this.tok.kind === 'op'
        ? ops.includes(this.tok.text)
        : ops.includes(this.tok.text) && this.tok.kind === 'ident'
    ) {
      const start = this.toks[this.i];
      const op = this.next().text;
      const right = next(id);
      left = make(op, left, right);
      if (start) this.markExpr(left, start, this.toks[this.i - 1] ?? start);
    }
    return left;
  }

  private orExpr(id: string): Expr {
    return this.binary(
      id,
      (x) => this.andExpr(x),
      ['||', 'or'],
      (_op, left, right) => ({
        k: 'logic',
        id: this.exprId(id),
        op: 'or' as LogicOp,
        left,
        right,
      }),
    );
  }

  private andExpr(id: string): Expr {
    return this.binary(
      id,
      (x) => this.eqExpr(x),
      ['&&', 'and'],
      (_op, left, right) => ({
        k: 'logic',
        id: this.exprId(id),
        op: 'and' as LogicOp,
        left,
        right,
      }),
    );
  }

  private eqExpr(id: string): Expr {
    return this.binary(
      id,
      (x) => this.relExpr(x),
      ['==', '!='],
      (op, left, right) => ({
        k: 'compare',
        id: this.exprId(id),
        op: COMPARE[op]!,
        left,
        right,
      }),
    );
  }

  private relExpr(id: string): Expr {
    return this.binary(
      id,
      (x) => this.addExpr(x),
      ['<', '<=', '>', '>='],
      (op, left, right) => ({
        k: 'compare',
        id: this.exprId(id),
        op: COMPARE[op]!,
        left,
        right,
      }),
    );
  }

  private addExpr(id: string): Expr {
    return this.binary(
      id,
      (x) => this.mulExpr(x),
      ['+', '-'],
      (op, left, right) => ({
        k: 'arith',
        id: this.exprId(id),
        op: op as ArithOp,
        left,
        right,
      }),
    );
  }

  private mulExpr(id: string): Expr {
    return this.binary(
      id,
      (x) => this.unary(x),
      ['*', '/', '%'],
      (op, left, right) => {
        const e: Expr = {
          k: 'arith',
          id: this.exprId(id),
          op: op === '%' ? 'mod' : (op as ArithOp),
          left,
          right,
        };
        if (op === '/' && this.doubleCast.has(left.id)) this.trueDiv.add(e.id);
        return e;
      },
    );
  }

  private unary(id: string): Expr {
    if (this.at('!') || this.at('not')) {
      const start = this.next();
      const arg = this.unary(id);
      const e: Expr = { k: 'not', id: this.exprId(id), arg };
      this.markExpr(e, start, this.toks[this.i - 1] ?? start);
      return e;
    }
    if (this.at('-')) {
      const start = this.next();
      const arg = this.unary(id);
      if (arg.k === 'num') return { ...arg, value: -arg.value };
      const e: Expr = {
        k: 'arith',
        id: this.exprId(id),
        op: '-',
        left: this.num(id, 0),
        right: arg,
      };
      this.markExpr(e, start, this.toks[this.i - 1] ?? start);
      return e;
    }
    if (this.eat('+')) return this.unary(id);
    const cast = this.castType();
    if (cast) {
      const arg = this.unary(id);
      if (cast === 'char') {
        // `(char) n` is the character with code n; `(char) toupper(c)` is already one.
        return this.staticType(arg) === 'char' ? arg : { k: 'chr', id: this.exprId(id), arg };
      }
      if (cast === 'int' && this.staticType(arg) === 'char') {
        return { k: 'code', id: this.exprId(id), arg };
      }
      if (cast === 'int') {
        // `(int) (a / b)` is how the C++ view writes whole-number division.
        if (arg.k === 'arith' && arg.op === '/') return { ...arg, op: 'idiv' };
        return arg;
      }
      // `(double) a / b` keeps real division even between whole numbers. The cast sits in front
      // of the left operand, so remember it and let the division claim it.
      if (arg.k === 'arith' && (arg.op === '/' || arg.op === 'idiv')) {
        this.trueDiv.add(arg.id);
        return { ...arg, op: '/' };
      }
      this.doubleCast.add(arg.id);
      return arg;
    }
    return this.postfix(id);
  }

  /** `(double)` / `(int)` before a value, without eating a plain parenthesis. */
  private castType(): ValueType | null {
    if (!this.at('(')) return null;
    const inner = this.toks[this.i + 1];
    const after = this.toks[this.i + 2];
    if (!inner || inner.kind !== 'ident' || after?.text !== ')') return null;
    const type = DECL_TYPE[inner.text];
    if (type === undefined) return null;
    this.next();
    this.next();
    this.next();
    return type;
  }

  /** `[i]` or `[i][j]` after a name. */
  private indexes(id: string): [Expr, Expr | null] {
    this.expect('[');
    const index = this.expression(id);
    this.expect(']');
    if (!this.eat('[')) return [index, null];
    const index2 = this.expression(id);
    this.expect(']');
    return [index, index2];
  }

  private postfix(id: string): Expr {
    let e = this.primary(id);
    for (;;) {
      if (this.at('[') && e.k === 'var') {
        const start = this.tok;
        const [index, index2] = this.indexes(id);
        const place: Expr = { k: 'at', id: this.exprId(id), name: e.name, index, index2 };
        this.markExpr(place, start, this.toks[this.i - 1] ?? start);
        e = place;
        continue;
      }
      if (this.at('.') && this.toks[this.i + 1]?.text === 'find') {
        const start = this.tok;
        this.next();
        this.next();
        this.expect('(');
        const needle = this.expression(id);
        const close = this.expect(')');
        e = { k: 'find', id: this.exprId(id), needle, hay: e };
        this.markExpr(e, start, close);
        continue;
      }
      if (this.at('.') && this.toks[this.i + 1]?.text === 'substr') {
        const start = this.tok;
        this.next();
        this.next();
        this.expect('(');
        const from = this.expression(id);
        // Without a length, the piece runs to the end of the text.
        const len: Expr = this.eat(',')
          ? this.expression(id)
          : {
              k: 'arith',
              id: this.exprId(id),
              op: '-',
              left: { k: 'length', id: this.exprId(id), arg: e },
              right: from,
            };
        const close = this.expect(')');
        e = { k: 'substr', id: this.exprId(id), arg: e, from, len };
        this.markExpr(e, start, close);
        continue;
      }
      if (
        this.at('.') &&
        this.toks[this.i + 1]?.text === 'size' &&
        e.k === 'var' &&
        this.declared.get(e.name) === 'string'
      ) {
        // `s.size()` on a text is its length, the same as `s.length()`.
        const start = this.tok;
        this.next();
        this.next();
        this.expect('(');
        const close = this.expect(')');
        e = { k: 'length', id: this.exprId(id), arg: e };
        this.markExpr(e, start, close);
        continue;
      }
      if (this.at('.') && this.toks[this.i + 1]?.text === 'size' && e.k === 'var') {
        const start = this.tok;
        this.next();
        this.next();
        this.expect('(');
        const close = this.expect(')');
        const sized: Expr = { k: 'size', id: this.exprId(id), name: e.name, dim: 1 };
        this.markExpr(sized, start, close);
        e = sized;
        continue;
      }
      if (this.at('.') && this.toks[this.i + 1]?.text === 'length') {
        const start = this.tok;
        this.next();
        this.next();
        this.expect('(');
        const close = this.expect(')');
        e = { k: 'length', id: this.exprId(id), arg: e };
        this.markExpr(e, start, close);
        continue;
      }
      return e;
    }
  }

  private primary(id: string): Expr {
    const t = this.tok;
    if (t.kind === 'number') {
      this.next();
      const e = this.num(id, Number(t.text));
      this.markExpr(e, t, t);
      return e;
    }
    if (t.kind === 'string') {
      this.next();
      const e = this.text(id, t.text);
      this.markExpr(e, t, t);
      return e;
    }
    if (t.kind === 'char') {
      this.next();
      const e: Expr = { k: 'text', id: this.exprId(id), value: t.text, char: true };
      this.markExpr(e, t, t);
      return e;
    }
    if (t.kind === 'ident') {
      if (t.text === 'true' || t.text === 'false') {
        this.next();
        const e: Expr = { k: 'bool', id: this.exprId(id), value: t.text === 'true' };
        this.markExpr(e, t, t);
        return e;
      }
      if (UNSUPPORTED_WORDS.has(t.text)) this.fail('unsupported', { what: t.text });
      this.next();
      // `string::npos` is what `find` gives when nothing is found; the blocks call that -1.
      if (t.text === 'string' && this.at(':') && this.toks[this.i + 1]?.text === ':') {
        this.next();
        this.next();
        this.expect('npos');
        return this.num(id, -1);
      }
      if (this.at('(')) {
        // to_string(x) and string(x) exist only to satisfy C++; the value is the same.
        if (t.text === 'to_string' || t.text === 'string') {
          this.next();
          let inner = this.expression(id);
          // `string(1, c)` is the character c as a text.
          if (t.text === 'string' && inner.k === 'num' && inner.value === 1 && this.eat(',')) {
            inner = this.expression(id);
          }
          this.expect(')');
          return inner;
        }
        if (t.text === 'toupper' || t.text === 'tolower') {
          this.next();
          const arg = this.expression(id);
          const close = this.expect(')');
          const e: Expr = { k: 'case', id: this.exprId(id), upper: t.text === 'toupper', arg };
          this.markExpr(e, t, close);
          return e;
        }
        this.fail('unsupported', { what: `${t.text}()` });
      }
      const e: Expr = { k: 'var', id: this.exprId(id), name: t.text };
      this.markExpr(e, t, t);
      return e;
    }
    if (this.at('(')) {
      this.next();
      const inner = this.expression(id);
      // `(cond ? "true" : "false")` is how a bool is printed; the value is the condition.
      if (this.eat('?')) {
        const a = this.expression(id);
        this.expect(':');
        const b = this.expression(id);
        this.expect(')');
        if (a.k === 'text' && b.k === 'text' && a.value === 'true' && b.value === 'false')
          return inner;
        this.fail('unsupported', { what: '?:' });
      }
      this.expect(')');
      return inner;
    }
    this.fail('expected', { expected: 'a value', found: t.text || 'end' });
  }

  // ---------- after the parse ----------

  result(body: Stmt[]): {
    program: Program;
    lineMap: Record<string, LineRange>;
    headMap: Record<string, LineRange>;
    spans: Record<string, Span>;
  } {
    // C++ reads `a / b` and `a + b` through the *declared* types: whole numbers divide to a whole
    // number, and adding text joins. Decide that first, then infer like the block path does.
    const program: Program = inferTypes({
      startId: 't:start',
      body: this.retype(body),
      variables: [],
      types: {},
      diagnostics: [],
    });
    const lines = Object.values(this.lineMap);
    const last = lines.reduce((m, r) => Math.max(m, r[1]), 1);
    this.lineMap['t:start'] = [1, last];
    this.headMap['t:start'] = [1, 1];
    return { program, lineMap: this.lineMap, headMap: this.headMap, spans: this.spans };
  }

  /** The static type C++ gives an expression, from the declarations in the text. */
  private staticType(e: Expr): ValueType {
    switch (e.k) {
      case 'num':
        return Number.isInteger(e.value) ? 'int' : 'double';
      case 'text':
        return e.char ? 'char' : 'string';
      case 'join':
      case 'substr':
        return 'string';
      case 'chr':
      case 'case':
        return 'char';
      case 'code':
      case 'find':
        return 'int';
      case 'bool':
      case 'compare':
      case 'logic':
      case 'not':
        return 'bool';
      case 'var':
        return this.declared.get(e.name) ?? 'int';
      case 'read':
        return e.as === 'text' ? 'string' : e.as;
      case 'length':
      case 'missing':
      case 'size':
        return 'int';
      case 'at': {
        const t = this.declared.get(e.name) ?? 'int';
        return t === 'string' ? 'char' : elemOf(t);
      }
      case 'arith': {
        if (e.op === 'idiv' || e.op === 'mod') return 'int';
        const both = this.staticType(e.left) === 'int' && this.staticType(e.right) === 'int';
        if (e.op === '/') return both && !this.trueDiv.has(e.id) ? 'int' : 'double';
        return both ? 'int' : 'double';
      }
    }
  }

  /** `a / b` between whole numbers is integer division; `a + b` with text is joining. */
  private retype(body: Stmt[]): Stmt[] {
    /** C++ does arithmetic on a char with its code: `c - 'a'` is `code(c) - code('a')`. */
    const numeric = (e: Expr): Expr =>
      this.staticType(e) === 'char' ? { k: 'code', id: `${e.id}/code`, arg: e } : e;
    const fixExpr = (e: Expr): Expr => {
      switch (e.k) {
        case 'arith': {
          const left = fixExpr(e.left);
          const right = fixExpr(e.right);
          if (
            e.op === '+' &&
            (this.staticType(left) === 'string' || this.staticType(right) === 'string')
          ) {
            return { k: 'join', id: e.id, left, right };
          }
          if (this.staticType(left) === 'char' || this.staticType(right) === 'char') {
            return { ...e, left: numeric(left), right: numeric(right) };
          }
          if (
            e.op === '/' &&
            !this.trueDiv.has(e.id) &&
            this.staticType(left) === 'int' &&
            this.staticType(right) === 'int'
          ) {
            return { ...e, op: 'idiv', left, right };
          }
          return left === e.left && right === e.right ? e : { ...e, left, right };
        }
        case 'compare': {
          const left = fixExpr(e.left);
          const right = fixExpr(e.right);
          // `c == 65` compares the character's code with a number.
          const l = this.staticType(left);
          const r = this.staticType(right);
          if (l === 'char' && (r === 'int' || r === 'double')) {
            return { ...e, left: numeric(left), right };
          }
          if (r === 'char' && (l === 'int' || l === 'double')) {
            return { ...e, left, right: numeric(right) };
          }
          return { ...e, left, right };
        }
        case 'logic':
        case 'join':
          return { ...e, left: fixExpr(e.left), right: fixExpr(e.right) };
        case 'not':
        case 'length':
        case 'code':
        case 'chr':
        case 'case':
          return { ...e, arg: fixExpr(e.arg) };
        case 'find':
          return { ...e, needle: fixExpr(e.needle), hay: fixExpr(e.hay) };
        case 'substr':
          return { ...e, arg: fixExpr(e.arg), from: fixExpr(e.from), len: fixExpr(e.len) };
        case 'at':
          return { ...e, index: fixExpr(e.index), index2: e.index2 ? fixExpr(e.index2) : null };
        default:
          return e;
      }
    };
    const fixStmts = (body: Stmt[]): Stmt[] =>
      body.map((s): Stmt => {
        switch (s.k) {
          case 'set':
            return { ...s, value: fixExpr(s.value) };
          case 'newArray':
            return { ...s, dims: s.dims.map(fixExpr) as [Expr] | [Expr, Expr] };
          case 'setAt':
            return {
              ...s,
              index: fixExpr(s.index),
              index2: s.index2 ? fixExpr(s.index2) : null,
              value: fixExpr(s.value),
            };
          case 'change':
            return { ...s, delta: fixExpr(s.delta) };
          case 'print':
            return { ...s, value: fixExpr(s.value) };
          case 'if':
            return {
              ...s,
              branches: s.branches.map((b) => ({ cond: fixExpr(b.cond), body: fixStmts(b.body) })),
              orElse: s.orElse ? fixStmts(s.orElse) : null,
            };
          case 'repeat':
            return { ...s, times: fixExpr(s.times), body: fixStmts(s.body) };
          case 'while':
            return { ...s, cond: fixExpr(s.cond), body: fixStmts(s.body) };
          case 'for':
            return { ...s, from: fixExpr(s.from), to: fixExpr(s.to), body: fixStmts(s.body) };
          case 'break':
            return s;
        }
      });
    return fixStmts(body);
  }
}

/** Parses C++ text into the same Program IR the blocks produce. Never throws. */
export function parseCpp(source: string): TextProgram {
  const empty = (errors: TextError[]): TextProgram => ({
    program: { startId: null, body: [], variables: [], types: {}, diagnostics: [] },
    code: { language: 'cpp', code: source, lineMap: {}, headMap: {} },
    spans: {},
    errors,
  });
  let parser: CppParser;
  try {
    parser = new CppParser(lex(source));
  } catch (e) {
    if (e instanceof LexError) return empty([e.info]);
    throw e;
  }
  try {
    const body = parser.parse();
    const { program, lineMap, headMap, spans } = parser.result(body);
    return {
      program,
      code: { language: 'cpp', code: source, lineMap, headMap },
      spans,
      errors: [],
    };
  } catch (e) {
    if (e instanceof Fail) return empty([e.info]);
    if (e instanceof LexError) return empty([e.info]);
    throw e;
  }
}
